<script setup lang="ts">
import type { ButtonTypes, AlignmentTypes } from "@/types";
import { toRefs, computed } from "vue";

export type ButtonSizeTypes = "xs" | "sm" | "md" | "lg";

interface Props {
  text?: string;
  disabled?: boolean;
  type?: ButtonTypes;
  size?: ButtonSizeTypes;
  icon?: string;
  fullWidth?: boolean;
  loading?: boolean;
  alignment?: AlignmentTypes;
  buttonType?: "submit" | "button";
  ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  text: undefined,
  disabled: false,
  type: "primary",
  size: "md",
  icon: undefined,
  fullWidth: false,
  loading: false,
  buttonType: "button",
});

const { text, type, size, disabled, icon, fullWidth, loading } = toRefs(props);
const isDisabled = computed(() =>
  disabled.value || loading.value ? "disabled" : "",
);
const iconOnly = computed(() => !text.value?.length && !!icon.value);
const iconSize = computed(() => {
  if (size.value === "lg") {
    return "text-lg";
  }

  return "text-md";
});

defineEmits<{ click: [Event] }>();

const textSize = computed(() => {
  if (size.value === "lg") {
    return "h500";
  }

  if (size.value === "xs") {
    return "h200";
  }

  return "h300";
});
</script>

<template>
  <button
    class="button cursor-pointer flex items-center select-none"
    :type="buttonType"
    :class="[
      type,
      size,
      isDisabled,
      { 'rounded-full iconOnly justify-center shadow-small-bottom': iconOnly },
      { 'rounded-lg': !iconOnly },
      { 'w-fit justify-center': !iconOnly && !fullWidth },
      { 'w-full justify-center': fullWidth },
      { 'has-icon': icon && icon.length },
      textSize,
    ]"
    :disabled="disabled || loading"
    :ariaLabel="ariaLabel"
    @click="(event: Event) => $emit('click', event)"
  >
    <slot name="icon">
      <i
        v-if="loading === false && icon && icon.length"
        :class="[iconSize, icon]"
        class="far"
      />
    </slot>
    <svg
      v-if="loading"
      aria-hidden="true"
      class="mr-2 animate-spin fill-darkOcean400 text-mango50 w-5 h-5"
      viewBox="0 0 101 101"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
        fill="currentColor"
      />
      <path
        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
        fill="currentFill"
      />
    </svg>
    <p>
      {{ text || "" }}
    </p>
  </button>
</template>

<style lang="scss">
.button {
  &.primary {
    background: theme("colors.darkOcean600");
    color: theme("colors.neutral50");
    transition: all 200ms;
    border: 1px solid theme("colors.darkOcean600");

    &:focus-visible {
      outline: 2px solid theme("colors.neutral1000");
      box-shadow: 0 0 0 1px #ffffff inset;
    }
    &.disabled {
      opacity: 0.6;
      pointer-events: none;
    }
    &:active {
      background: theme("colors.darkOcean600");
    }
  }
  &.white {
    background: theme("colors.neutral0");
    color: theme("colors.neutral1000");
    border: 1px solid theme("colors.neutral300");
    &:hover {
      background: theme("colors.neutral100");
    }
    &:focus-visible {
      outline: 2px solid theme("colors.neutral1000");
      box-shadow: 0 0 0 1px #ffffff inset;
    }
    &.disabled {
      color: theme("colors.neutral500");
      pointer-events: none;
    }
    &:active {
      background: theme("colors.neutral300");
    }
  }

  &.green {
    background: theme("colors.green.400");
    color: theme("colors.neutral0");
    &:hover {
      background: theme("colors.green.500");
    }
    &:focus-visible {
      outline: 2px solid theme("colors.neutral1000");
      box-shadow: 0 0 0 1px #ffffff inset;
    }
    &.disabled {
      background: theme("colors.green.200");
      color: theme("colors.neutral0");
      pointer-events: none;
    }
    &:active {
      background: theme("colors.green.400");
    }
  }
  &.grey {
    background: theme("colors.neutral100");

    &:focus-visible {
      outline: 2px solid theme("colors.neutral1000");
      box-shadow: 0 0 0 1px theme("colors.neutral0") inset;
    }
  }
  &.error {
    background: theme("colors.red400");
    color: theme("colors.neutral50");
    &:hover {
      background: theme("colors.red400");
    }
    &:focus-visible {
      outline: 2px solid theme("colors.neutral1000");
      box-shadow: 0 0 0 1px #ffffff inset;
    }
    &.disabled {
      background: theme("colors.red200");
      pointer-events: none;
    }
    &:active {
      background: theme("colors.red400");
    }
  }
  &.xs {
    &:not(.has-icon) {
      padding: 0.5rem 1rem;
    }

    &.has-icon {
      padding: 0.5rem 0.75rem 0.5rem 0.5rem;
    }

    &:not(.iconOnly) {
      gap: 0.25rem;
    }

    &.iconOnly {
      width: 2rem;
      height: 2rem;
      padding: 0;
    }
  }
  &.sm {
    &:not(.has-icon) {
      padding: 0.5rem 1rem;
    }

    &.has-icon {
      padding: 0.5rem 0.75rem 0.5rem 0.5rem;
    }

    &:not(.iconOnly) {
      gap: 0.25rem;
    }

    &.iconOnly {
      width: 2rem;
      height: 2rem;
      padding: 0;
    }
  }
  &.md {
    &:not(.has-icon) {
      padding: 0.75rem 1rem;
    }

    &.has-icon {
      padding: 0.75rem 1rem 0.75rem 0.75rem;
    }

    &:not(.iconOnly) {
      gap: 0.5rem;
    }

    &.iconOnly {
      width: 2.5rem;
      height: 2.5rem;
      padding: 0;
    }
  }
  &.lg {
    &:not(.has-icon) {
      padding: 1rem 1.5rem;
    }

    &.has-icon {
      padding: 1rem 1.5rem 1rem 1rem;
    }

    &:not(.iconOnly) {
      gap: 0.5rem;
    }

    &.iconOnly {
      width: 3rem;
      height: 3rem;
      padding: 1rem;
    }
  }

  & svg.font-20 {
    font-size: 1.25rem;
  }

  & svg.font-24 {
    font-size: 1.5rem;
  }

  &.inverse-focus-outline {
    &:focus-visible {
      outline: 2px solid theme("colors.neutral0");
      outline-offset: 1px;
    }
  }
}
</style>
